import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLocale } from "@/lib/i18n";
import { Choice, Repeat, Typed, YesNo } from "./Controls";

function show(q, v, pick, t) {
  if (v === null || v === undefined || v === "") return "—";
  if (q.type === "yesno") return v ? t("yes") : t("no");
  if (q.type === "choice") return pick((q.options.find((o) => o.value === v) || {}).label) || v;
  if (q.type === "group") return Object.values(v).filter(Boolean).join(", ");
  if (q.type === "repeat") return `${v.length}`;
  return String(v);
}

function Toggle() {
  const { lang, setLang } = useLocale();
  return (
    <div className="flex items-center gap-2 text-sm" data-testid="language-toggle">
      {[["en", "English"], ["ur", "اردو"]].map(([k, l]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`language-toggle-${k}`} className={lang === k ? "font-semibold text-navy underline underline-offset-4" : "text-muted hover:text-navy"}>{l}</button>
      ))}
    </div>);
}

export default function Interview({ caseId, onFullForm, onNavigate, onUnavailable, onChanged }) {
  const { t, pick, pk, lang, rtl } = useLocale();
  const [st, setSt] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/interview`).then((r) => setSt(r.data)).catch((e) => { if (e.response?.status === 404) onUnavailable(); else setSt(false); });
  }, [caseId, onUnavailable]);
  useEffect(() => { load(); }, [load]);

  const submit = async (q, value) => {
    setBusy(true); setErr(null);
    try { const r = await api.post(`/cases/${caseId}/interview/answer`, { id: q.id, value }); setSt(r.data); setEditId(null); onChanged(); }
    catch (e) { const d = e.response?.data?.detail; setErr(d && typeof d === "object" ? pick(d) : "Could not save"); }
    finally { setBusy(false); }
  };
  const skip = async (q) => {
    setBusy(true);
    try { const r = await api.post(`/cases/${caseId}/interview/skip`, { id: q.id }); setSt(r.data); onChanged(); }
    catch (e) { toast.error("Could not skip"); } finally { setBusy(false); }
  };

  if (st === false) return <p className="text-sm text-muted">Could not load the interview.</p>;
  if (!st) return <div className="h-40 animate-pulse bg-white" />;

  const cur = st.questions.find((q) => q.id === (editId || st.current_id));
  const before = st.questions.filter((q) => q.status !== "pending" && q.id !== cur?.id);
  const shown = showAll ? before : before.slice(-3);
  const pct = Math.round((st.progress.done / Math.max(st.progress.total, 1)) * 100);
  const urdu = lang === "ur";

  return (
    <div dir={rtl ? "rtl" : "ltr"} lang={lang} className={`max-w-3xl ${urdu ? "font-urdu leading-loose" : ""}`} data-testid="interview-panel">
      {pk && (
        <div className="mb-6 border-s-4 border-brandyellow bg-white px-5 py-4" data-testid="interview-greeting">
          <p className="font-serif text-xl text-heading">{t("greeting")}{!urdu && <span className="font-urdu text-lg"> — السلام علیکم</span>}</p>
          <p className="mt-1 text-sm text-muted">{t("greetSub")}</p>
        </div>
      )}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted" data-testid="interview-progress">{t("question")} {Math.min(st.progress.done + 1, st.progress.total)} {t("of")} {st.progress.total}</p>
        <div className="flex items-center gap-5">
          {pk && <Toggle />}
          <button onClick={onFullForm} className="text-sm text-tblue underline underline-offset-4" data-testid="full-form-switch-btn">{t("fullForm")}</button>
        </div>
      </div>
      <div className="mb-6 h-1 bg-navy/10"><div className="h-1 bg-navy" style={{ width: `${pct}%` }} /></div>

      {before.length > shown.length || showAll ? (
        <button onClick={() => setShowAll(!showAll)} className="mb-3 text-xs text-muted underline" data-testid="interview-toggle-earlier">{showAll ? t("hide") : t("earlier")}</button>
      ) : null}
      <ul className="mb-6 space-y-2" data-testid="interview-thread">
        {shown.map((q) => (
          <li key={q.id} className="flex items-start justify-between gap-4 border-b border-navy/10 pb-2" data-testid={`answered-${q.id}`}>
            <div className="min-w-0">
              <p className="text-xs text-muted">{pick(q.text)}</p>
              <p className="mt-0.5 break-words text-sm font-medium text-navy">{q.status === "skipped" ? "—" : show(q, q.value, pick, t)}</p>
            </div>
            <button onClick={() => { setEditId(q.id); setErr(null); }} className="shrink-0 text-xs text-tblue underline" data-testid={`edit-${q.id}`}>{t("edit")}</button>
          </li>
        ))}
      </ul>

      {cur ? (
        <section className="border border-navy/15 bg-white p-6" data-testid={`question-${cur.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">{pick(st.sections[cur.section])}</p>
          <h2 className="mt-2 font-serif text-2xl leading-snug text-heading" data-testid="interview-question-text">{pick(cur.text)}</h2>
          {pick(cur.help) && <p className="mt-2 text-sm text-muted">{pick(cur.help)}</p>}
          {pk && !urdu && (
            <div className="mt-3 border-s-2 border-brandyellow bg-softcream px-3 py-2 text-sm font-urdu leading-loose text-navy" dir="rtl" lang="ur" data-testid="interview-urdu-help">
              {cur.text.ur}{cur.help.ur ? ` — ${cur.help.ur}` : ""}
            </div>
          )}
          {pk && urdu && <p className="mt-2 text-xs text-muted" dir="ltr" lang="en">{cur.text.en}</p>}
          <div className="mt-5">
            {cur.type === "choice" && <Choice options={cur.options} value={cur.value} onPick={(v) => submit(cur, v)} />}
            {cur.type === "yesno" && <YesNo value={cur.value} onPick={(v) => submit(cur, v)} />}
            {cur.type === "repeat" && <Repeat key={cur.id} q={cur} value={cur.value} onSubmit={(v) => submit(cur, v)} busy={busy} />}
            {["text", "textarea", "number", "date", "group"].includes(cur.type) && (
              <Typed key={cur.id} q={cur} value={cur.value} onSubmit={(v) => submit(cur, v)} onSkip={() => skip(cur)} busy={busy} />
            )}
          </div>
          {err && <p className="mt-3 text-sm text-coral" data-testid="interview-error">{err}</p>}
          {editId && <button onClick={() => setEditId(null)} className="mt-4 text-xs text-muted underline" data-testid="edit-cancel">←</button>}
        </section>
      ) : (
        <section className="border border-navy/15 bg-white p-6" data-testid="interview-complete">
          <h2 className="font-serif text-2xl text-heading">{t("done")}</h2>
          <p className="mt-2 text-sm text-muted">{t("doneSub")}</p>
          <div className="mt-5 flex gap-3">
            <button onClick={() => onNavigate("letters")} className="rounded bg-navy px-5 py-2.5 text-sm font-semibold text-white" data-testid="interview-to-letters">{t("letters")}</button>
            <button onClick={() => onNavigate("checks")} className="rounded border border-navy/25 bg-white px-5 py-2.5 text-sm font-medium text-navy" data-testid="interview-to-checks">{t("checks")}</button>
          </div>
        </section>
      )}

      {st.notes.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="interview-notes" dir="ltr" lang="en">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted">{t("notes")}</p>
          {st.notes.map((n) => (
            <div key={n.id} className="border border-navy/10 border-l-4 border-l-coral bg-white p-4" data-testid={`note-${n.id}`}>
              <p className="text-sm font-medium text-heading">{n.title}</p>
              <p className="mt-1 text-xs text-muted">{n.why} {n.fix}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
